import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Input } from "@/components/ui/input";

export default function ThankYou() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [email, setEmail] = useState(location.state?.email || "");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const join = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error(t("err_email"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/early-access", { email, name });
      setDone(true);
      toast.success(t("ty_added"));
    } catch (e) {
      toast.error(t("submit_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0A0F1C]">
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-violet-600/20 blur-[150px]" />
      <div className="relative z-10 mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center px-6 py-16 text-center">
        <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 16 }}
          className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 shadow-lg shadow-emerald-900/40">
          <CheckCircle2 className="h-10 w-10 text-white" />
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="mt-8 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl" data-testid="thankyou-heading">
          {t("ty_heading")}
        </motion.h1>

        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="mt-5 max-w-xl text-base leading-relaxed text-slate-300">
          {t("ty_body")}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="mt-10 w-full rounded-3xl border border-white/10 bg-[#0F1629] p-6 sm:p-8">
          {done ? (
            <div className="py-4" data-testid="early-access-done">
              <Sparkles className="mx-auto h-8 w-8 text-violet-400" />
              <p className="mt-3 font-display text-xl font-semibold text-white">{t("ty_done_t")}</p>
              <p className="mt-2 text-sm text-slate-400">{t("ty_done_b")}</p>
            </div>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-violet-500/10 px-3 py-1 text-xs font-medium text-violet-300">
                <Sparkles className="h-3.5 w-3.5" /> {t("ty_badge")}
              </div>
              <h2 className="mt-4 font-display text-xl font-bold text-white">{t("ty_join_title")}</h2>
              <p className="mt-2 text-sm text-slate-400">{t("ty_join_sub")}</p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <Input data-testid="early-access-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("ty_name")}
                  className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500" />
                <Input data-testid="early-access-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("ty_email")}
                  className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500" />
                <button data-testid="early-access-submit" onClick={join} disabled={loading}
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {t("ty_join")}
                </button>
              </div>
            </>
          )}
        </motion.div>

        <button data-testid="back-home-btn" onClick={() => navigate("/")}
          className="mt-8 text-sm text-slate-400 underline-offset-4 hover:text-white hover:underline">
          {t("back_home")}
        </button>
      </div>
    </div>
  );
}
